import { isUnaryFilterOperator, type AttributeFilterState } from '@/features/dryad';
import { queryEncode } from '@/utils/condition-query-encoding';
import { isEmptyString } from '@/utils/string';

/**
 * The one attribute condition a recipient search by filters asks about.
 *
 * Ember's modal (`customer-search`) serialises its condition-list row with
 * `serializeLegacyFilter(condition, false, true)` and hands
 * `queryEncode([condition])` to the customers endpoint as `filters`. That is
 * the legacy condition-array wire, not the people list's proto envelope; the
 * endpoint reads both, and this sends the bytes Ember sends so the two apps
 * ask the same question.
 */

/** The operators whose value Ember refuses to leave blank (`allowBlankPredicate: false`). */
const VALUE_REQUIRED_OPERATORS = new Set(['eq', '!eq']);

/** Why the condition cannot be searched with yet, in Ember's wording; empty when it can. */
export function recipientFilterErrors(condition: AttributeFilterState): string[] {
  const errors: string[] = [];
  if (isEmptyString(condition.field)) {
    errors.push("Attribute can't be blank.");
  }
  if (VALUE_REQUIRED_OPERATORS.has(condition.operator) && isEmptyString(condition.value)) {
    errors.push("Value can't be blank.");
  }
  return errors;
}

/**
 * The errors as one sentence, the way Ember's condition editor joins them for
 * its Apply tooltip: each message's full stop dropped, comma-separated, and
 * one full stop at the end.
 */
export function recipientFilterErrorMessage(errors: readonly string[]): string {
  if (errors.length === 0) {
    return '';
  }
  return `${errors.map((error) => error.replace(/\.$/, '')).join(', ')}.`;
}

/**
 * The `filters` param for a condition search: a one-row legacy condition
 * array, Base64-encoded.
 *
 * The row keeps Ember's key order and spelling — the operator in its segment
 * vocabulary with any negation as a `!` prefix (no `inverse` flag), the field
 * and value trimmed, and a null value for an operator that takes none.
 */
export function encodeRecipientFilters(condition: AttributeFilterState): string {
  const row = {
    field: condition.field.trim(),
    operator: condition.operator,
    value: isUnaryFilterOperator(condition.operator) ? null : (condition.value?.trim() ?? null),
    type: 'attribute',
  };
  return queryEncode([row]);
}
